// The Source books filter, enhanced. Without this file the panel is simply
// open, the trigger is hidden, and the form is a plain GET with a "Show books"
// button — everything still works.
//
// With it, the options move into a popout and a change submits at once, into
// the results frame only. The form is never replaced, so focus stays on the
// checkbox or radio the reader just used; replacing the whole page would throw
// a keyboard user back to <body> on every tick.
//
// The panel is a button with aria-expanded and a MOUNTED panel, not a
// role="menu": the options are independent, so it stays open while several are
// changed, and unmounting it would discard what the reader had set.
const SEARCH_DELAY = 250;

function toSentence(items) {
  if (items.length <= 1) return items.join("");
  if (items.length === 2) return items.join(" and ");
  return `${items.slice(0, -1).join(", ")} and ${items.at(-1)}`;
}

// Everything chosen INSIDE the popout, named — the same set
// SourceBooksHelper#active_filter_labels writes on the server. The query is
// not here: the search box sits in the bar with its value in it. Ordering and
// grouping are display choices and are left out too.
function chosenLabels(form) {
  return [...form.querySelectorAll("input[type=checkbox]:checked")]
    .map((input) => input.dataset.label)
    .filter(Boolean);
}

function summarise(form) {
  const chosen = chosenLabels(form);
  const summary = form.querySelector("[data-filter-summary]");
  if (summary) summary.textContent = chosen.length ? toSentence(chosen) : "Every book";

  const count = form.querySelector("[data-filter-count]");
  if (count) {
    count.textContent = String(chosen.length);
    count.hidden = chosen.length === 0;
  }
}

function setUpPanel(form) {
  const trigger = form.querySelector("[data-source-books-trigger]");
  const panel = form.querySelector("[data-source-books-panel]");
  if (!trigger || !panel) return;

  const show = (open) => {
    panel.hidden = !open;
    trigger.setAttribute("aria-expanded", String(open));
  };

  trigger.hidden = false;
  show(false);
  trigger.addEventListener("click", () => show(panel.hidden));

  // Escape closes and returns focus to the trigger: that is where the reader
  // opened it from.
  form.addEventListener("keydown", (event) => {
    if (event.key !== "Escape" || panel.hidden) return;
    show(false);
    trigger.focus();
  });

  // An outside CLICK closes without moving focus — the reader has just put it
  // somewhere.
  document.addEventListener("pointerdown", (event) => {
    if (!panel.hidden && !form.contains(event.target)) show(false);
  });
}

export function enhance(form) {
  if (form.dataset.sourceBooksReady) return;
  form.dataset.sourceBooksReady = "true";

  const apply = form.querySelector("[data-source-books-apply]");
  if (apply) apply.hidden = true;

  setUpPanel(form);

  let timer;
  const submit = () => {
    clearTimeout(timer);
    form.requestSubmit();
  };

  form.addEventListener("change", (event) => {
    summarise(form);
    if (event.target.type !== "search") submit();
  });
  form.addEventListener("input", (event) => {
    if (event.target.type !== "search") return;
    clearTimeout(timer);
    timer = setTimeout(submit, SEARCH_DELAY);
  });
}

export function start() {
  document.querySelectorAll("[data-source-books-filter]").forEach(enhance);
}

// Back and Forward must leave the list saying what the URL says. The results
// frame advances the URL, and Turbo's restoration painted the PREVIOUS
// filter's list under the new address — "37 books of 329" under a bare
// /source-books, unchanged through 44 polls across 20 seconds.
//
// A reload here and nowhere else. Making the whole page reload on every visit
// (turbo-visit-control) fixed this and threw focus off the checkbox on every
// tick of a filter; ticking a filter still never reloads.
window.addEventListener("popstate", () => {
  if (!document.querySelector("[data-source-books-filter]")) return;
  window.location.reload();
});

document.addEventListener("DOMContentLoaded", start);
document.addEventListener("turbo:load", start);
